import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { collection, addDoc, serverTimestamp, updateDoc, doc, arrayUnion, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { useAuth } from 'contexts/AuthContext';
import ModernMessageBubble from '../messaging/ModernMessageBubble';
import TimestampSeparator from '../messaging/TimestampSeparator';
import EmojiPicker from '../messaging/EmojiPicker';
// import TypingIndicator from '../messaging/TypingIndicator';
// import OnlineStatusIndicator, { MultipleOnlineStatus } from '../messaging/OnlineStatusIndicator';
// import { useUserPresence } from '../../hooks/useUserPresence';
import Icon from 'components/AppIcon';
import { toast } from 'sonner';
import { groupMessages, organizeMessageThreads, flattenMessageThreads } from '../../utils/messageGrouping';

const ConversationThread = ({ conversation, onBack }) => {
  const { user, profile } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  const searchInputRef = useRef(null);
  const messageInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Initialize presence system (DISABLED - Firebase permissions issues)
  // const { startTyping, stopTyping } = useUserPresence();
  const startTyping = () => {}; // Placeholder
  const stopTyping = () => {}; // Placeholder
  
  // Get participant IDs for online status (DISABLED)
  // const participantIds = useMemo(() => {
  //   return Array.from(conversation.participants || []).filter(id => id !== user?.uid);
  // }, [conversation.participants, user?.uid]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation.messages]);

  // Mark messages as read
  useEffect(() => {
    const markAsRead = async () => {
      const unreadMessages = conversation.messages.filter(
        msg => !msg.read_by?.includes(user.uid) && msg.author_id !== user.uid
      );

      for (const message of unreadMessages) {
        try {
          await updateDoc(doc(db, 'comments', message.id), {
            read_by: arrayUnion(user.uid)
          });
        } catch (error) {
          console.error('Error marking message as read:', error);
        }
      }
    };

    if (conversation.messages.length > 0) {
      markAsRead();
    }
  }, [conversation.messages, user.uid]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageContent = newMessage.trim();
    const tempId = `temp-${Date.now()}`;
    setSending(true);
    
    // Stop typing indicator
    stopTyping(conversation.item_id);
    
    // Create optimistic message for immediate display
    const optimisticMessage = {
      id: tempId,
      item_id: conversation.item_id,
      item_type: conversation.item_type,
      item_title: conversation.item_title,
      content: messageContent,
      author_id: user.uid,
      author_name: profile?.full_name || user.email,
      author_role: profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : 'client',
      created_at: new Date(),
      parent_id: replyingTo,
      read_by: [user.uid],
      reactions: {},
      edited: false,
      pinned: false,
      _optimistic: true // Flag to identify optimistic messages
    };

    // Add optimistic message immediately
    conversation.messages = [...(conversation.messages || []), optimisticMessage];
    
    // Clear input immediately for better UX
    setNewMessage('');
    setReplyingTo(null);

    // Send to Firebase 
    try {
      const docRef = await addDoc(collection(db, 'comments'), {
        item_id: conversation.item_id,
        item_type: conversation.item_type,
        item_title: conversation.item_title,
        content: messageContent,
        author_id: user.uid,
        author_name: profile?.full_name || user.email,
        author_role: profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : 'client',
        created_at: serverTimestamp(),
        parent_id: replyingTo,
        read_by: [user.uid],
        reactions: {},
        edited: false,
        pinned: false
      });
      
      // Remove optimistic message once real message arrives (via real-time listener)
      setTimeout(() => {
        conversation.messages = conversation.messages.filter(msg => msg.id !== tempId);
      }, 2000);
      
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      // Remove failed optimistic message
      conversation.messages = conversation.messages.filter(msg => msg.id !== tempId);
    } finally {
      setSending(false);
    }
  };

  // Add emoji to message
  const handleEmojiSelect = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'text-red-400';
      case 'writer': return 'text-green-400';
      case 'qa': return 'text-blue-400';
      case 'client': return 'text-purple-400';
      default: return 'text-gray-400';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return 'Shield';
      case 'writer': return 'PenTool';
      case 'qa': return 'CheckCircle';
      case 'client': return 'User';
      default: return 'User';
    }
  };

  const isOwnMessage = (message) => message.author_id === user.uid;

  // Process messages with modern grouping and threading
  const processedMessages = useMemo(() => {
    if (!conversation.messages || conversation.messages.length === 0) return [];
    
    let messages = conversation.messages;
    
    // Filter messages by search query
    if (searchQuery.trim()) {
      messages = messages.filter(message => 
        message.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        message.author_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // First organize threads
    const threadedMessages = organizeMessageThreads(messages);
    
    // Then flatten for display with thread context
    const flatMessages = flattenMessageThreads(threadedMessages);
    
    // Finally group by time and sender
    return groupMessages(flatMessages);
  }, [conversation.messages, searchQuery]);

  // Handle swipe to reply
  const handleSwipeReply = (message) => {
    setReplyingTo(message.id);
    // Add haptic feedback on mobile
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  // Handle context menu
  const handleContextMenu = (message, position) => {
    // Could implement additional context menu logic here
    console.log('Context menu for message:', message.id, 'at position:', position);
  };

  // Delete entire chat conversation
  const handleDeleteChat = async () => {
    if (!window.confirm('Are you sure you want to delete this entire conversation? This action cannot be undone.')) {
      return;
    }

    try {
      const q = query(
        collection(db, 'comments'),
        where('item_id', '==', conversation.item_id),
        where('item_type', '==', conversation.item_type)
      );
      
      const querySnapshot = await getDocs(q);
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      
      await Promise.all(deletePromises);
      toast.success('Conversation deleted successfully');
      onBack();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    }
    setShowOptionsMenu(false);
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-white/5 to-transparent">
      {/* Header */}
      <div className="mobile-conversation-header flex items-center gap-3 p-4 border-b border-white/10 bg-white/5">
        <button
          onClick={onBack}
          className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
        >
          <Icon name="ArrowLeft" size={20} className="text-white/80" />
        </button>
        
        <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
          <Icon 
            name={conversation.item_type === 'assignment' ? 'FileText' : 'GraduationCap'} 
            size={20} 
            className="text-primary" 
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <h2 className="text-white font-semibold truncate">{conversation.item_title}</h2>
          <div className="flex items-center space-x-2">
            <p className="text-white/60 text-sm capitalize">
              {conversation.item_type} • {conversation.participants.size} participant{conversation.participants.size !== 1 ? 's' : ''}
            </p>
            {/* Online status temporarily disabled due to Firebase permissions */}
            {/* {participantIds.length > 0 && (
              <MultipleOnlineStatus userIds={participantIds} maxDisplay={2} />
            )} */}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={() => {
              setShowSearchBar(!showSearchBar);
              if (!showSearchBar) {
                setSearchQuery('');
              }
            }}
            className={`p-2 hover:bg-white/10 rounded-lg transition-colors ${
              showSearchBar ? 'bg-primary/20 text-primary' : 'text-white/60'
            }`}
          >
            <Icon name="Search" size={18} />
          </button>
          <div className="relative">
            <button 
              onClick={() => setShowOptionsMenu(!showOptionsMenu)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <Icon name="MoreVertical" size={18} className="text-white/60" />
            </button>
            
            <AnimatePresence>
              {showOptionsMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -10 }}
                  className="absolute top-full right-0 mt-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg py-1 shadow-xl z-50 min-w-[160px]"
                >
                  <button
                    onClick={handleDeleteChat}
                    className="w-full px-3 py-2 text-left hover:bg-red-500/20 transition-colors flex items-center space-x-2 text-sm text-red-400 hover:text-red-300"
                  >
                    <Icon name="Trash2" size={14} />
                    <span>Delete Conversation</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Click outside to close */}
            {showOptionsMenu && (
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowOptionsMenu(false)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <AnimatePresence>
        {showSearchBar && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 py-3 border-b border-white/10 bg-white/5"
          >
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search messages..."
                className="w-full pl-10 pr-10 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none text-sm"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                >
                  <Icon name="X" size={16} />
                </button>
              )}
            </div>
            {searchQuery && (
              <div className="mt-2 text-xs text-white/60">
                {processedMessages.filter(item => item.type === 'message').length} message{processedMessages.filter(item => item.type === 'message').length !== 1 ? 's' : ''} found
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        <AnimatePresence mode="popLayout">
          {processedMessages.map((item, index) => {
            if (item.type === 'timestamp') {
              return (
                <TimestampSeparator
                  key={item.id}
                  timestamp={item.content}
                />
              );
            }

            return (
              <ModernMessageBubble
                key={item.id}
                message={item}
                previousMessage={processedMessages[index - 1]?.type === 'message' ? processedMessages[index - 1] : null}
                nextMessage={processedMessages[index + 1]?.type === 'message' ? processedMessages[index + 1] : null}
                currentUser={user}
                onReply={(message) => setReplyingTo(message.id)}
                onSwipeReply={handleSwipeReply}
                onContextMenu={handleContextMenu}
                isLastInGroup={item.isLastInGroup}
                showAvatar={item.showAvatar}
                showTimestamp={item.showTimestamp}
              />
            );
          })}
        </AnimatePresence>
        
        {/* Typing Indicator - Temporarily disabled due to Firebase permissions */}
        {/* <TypingIndicator conversationId={conversation.item_id} /> */}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Reply Indicator */}
      {replyingTo && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="px-4 py-3 bg-white/5 border-t border-white/10 backdrop-blur-sm"
        >
          {(() => {
            const replyMessage = conversation.messages.find(m => m.id === replyingTo);
            return (
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="Reply" size={14} className="text-primary flex-shrink-0" />
                    <span className="text-primary text-sm font-medium">
                      Replying to {replyMessage?.author_name}
                    </span>
                  </div>
                  {replyMessage && (
                    <div className="bg-white/10 border-l-3 border-primary/50 rounded-lg p-2 ml-5">
                      <p className="text-white/70 text-sm line-clamp-2">
                        {replyMessage.content}
                      </p>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setReplyingTo(null)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
                >
                  <Icon name="X" size={16} className="text-white/60" />
                </button>
              </div>
            );
          })()}
        </motion.div>
      )}

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="relative p-4 border-t border-white/10 bg-white/5">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <textarea
              ref={messageInputRef}
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                
                // Handle typing indicators
                if (e.target.value.trim()) {
                  startTyping(conversation.item_id);
                  
                  // Clear existing timeout
                  if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                  }
                  
                  // Stop typing after 2 seconds of inactivity
                  typingTimeoutRef.current = setTimeout(() => {
                    stopTyping(conversation.item_id);
                  }, 2000);
                } else {
                  stopTyping(conversation.item_id);
                }
              }}
              placeholder="Type a message..."
              className="mobile-message-input w-full p-3 pr-12 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              rows={1}
              maxLength={1000}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              onBlur={() => {
                // Stop typing when input loses focus
                setTimeout(() => stopTyping(conversation.item_id), 1000);
              }}
            />
          </div>
          
          {/* Emoji Picker Button */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="p-3 bg-white/10 hover:bg-white/20 text-white/60 hover:text-white rounded-xl transition-colors flex-shrink-0"
            >
              <Icon name="Smile" size={18} />
            </button>
            
            <EmojiPicker 
              isOpen={showEmojiPicker}
              onClose={() => setShowEmojiPicker(false)}
              onEmojiSelect={handleEmojiSelect}
            />
          </div>
          
          <button
            type="submit"
            disabled={sending || !newMessage.trim()}
            className="p-3 bg-primary hover:bg-primary/80 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            {sending ? (
              <Icon name="Loader2" size={18} className="animate-spin" />
            ) : (
              <Icon name="Send" size={18} />
            )}
          </button>
        </div>
        
        {newMessage.length > 0 && (
          <div className="flex justify-between items-center mt-2 text-xs text-white/60">
            <div className="flex items-center gap-2">
              <span>Press Enter to send, Shift+Enter for new line</span>
            </div>
            <span>{newMessage.length}/1000</span>
          </div>
        )}
      </form>
      
    </div>
  );
};

export default ConversationThread;